'use client';

/**
 * ConfirmDialog — Phase A7 destructive-action confirmation primitive.
 *
 * Standard pattern for "Are you sure?" prompts. Three variants:
 *
 * 1. `simple` (default) — title + description + Cancel/Confirm buttons.
 *    Use for low-stakes confirmations ("Discard draft?", "Mark resolved?").
 *
 * 2. `destructive` — same shape + red Confirm button + optional
 *    `typeToConfirmWord` requiring the user to type a specific word
 *    (typically the entity name) before the button enables. Use for
 *    high-stakes destructive actions (Delete client, Revoke access,
 *    Cancel contract).
 *
 * 3. `many-record` — danger action with a visible record name and count.
 *
 * Composes on top of the existing Dialog primitive in this package
 * — the dialog chrome, focus management, escape-to-close, and overlay
 * are owned by Dialog. ConfirmDialog adds the type-to-confirm gate
 * and the standard button styling.
 *
 * When to use which (Phase A7 decision matrix):
 * - Drawer    → long form, multi-step workflow, contextual edit
 * - Modal     → short confirmation, single decision (this primitive)
 * - Toast     → action confirmation, time-bounded info
 * - Confirm   → destructive actions, "type X to confirm" gate
 */

import React, { useId, useState } from 'react';
import { Dialog, DialogFooter } from '../dialog';
import { buttonVariants } from '../button-variants';
import { cn } from '../../lib/utils';

export interface ConfirmDialogProps {
  /** Controlled open state. */
  open: boolean;
  /** Called when the dialog closes (Cancel, overlay click, Esc). */
  onClose: () => void;
  /**
   * Called when the user confirms (clicks the primary button OR presses
   * Enter while the input is valid). The dialog does NOT auto-close —
   * the consumer is expected to call `onClose()` once the action either
   * completes or surfaces an error.
   */
  onConfirm: () => void;
  /** Title — "Delete Bob's account?", "Discard draft?". */
  title: string;
  /** Body copy explaining what's about to happen. */
  description?: React.ReactNode;
  /** Label for the primary action. Defaults to "Confirm". */
  confirmLabel?: string;
  /** Label for the cancel action. Defaults to "Cancel". */
  cancelLabel?: string;
  /**
   * Variant — `destructive` styles the confirm button red and enables
   * the optional type-to-confirm gate.
   */
  variant?: 'simple' | 'destructive' | 'many-record';
  /** Name of the record or record type affected by this action. */
  recordName?: string;
  /** Number of affected records for a many-record confirmation. */
  recordCount?: number;
  /**
   * When set on a `destructive` variant, the user must type this exact
   * string before the confirm button enables (case-sensitive). Typical
   * pattern: pass the entity name ("Bob Smith", "Madea Park").
   */
  typeToConfirmWord?: string;
  /**
   * Loading flag for in-flight confirms — disables the primary button
   * + shows a "…" suffix. The dialog stays open while loading; the
   * consumer must call `onClose()` to dismiss.
   */
  loading?: boolean;
  /** Disable confirmation while a read is pending; Cancel and Escape remain available. */
  confirmDisabled?: boolean;
}

export function ConfirmDialog({
  open,
  onClose,
  onConfirm,
  title,
  description,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  variant = 'simple',
  typeToConfirmWord,
  recordName,
  recordCount,
  loading,
  confirmDisabled = false,
}: ConfirmDialogProps) {
  const [typedValue, setTypedValue] = useState('');
  const inputId = useId();
  // Reset typed value whenever the dialog re-opens to avoid carrying
  // a stale entry across sessions.
  const previousOpenRef = React.useRef(open);
  React.useEffect(() => {
    if (open && !previousOpenRef.current) {
      setTypedValue('');
    }
    previousOpenRef.current = open;
  }, [open]);

  const requiresTyping = variant === 'destructive' && !!typeToConfirmWord;
  const typingValid = !requiresTyping || typedValue === typeToConfirmWord;
  const canConfirm = !loading && !confirmDisabled && typingValid;

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && canConfirm) {
      e.preventDefault();
      onConfirm();
    }
  };

  // Codex Phase A Sprint 3 pass 1 P2: forbid dismissal while a
  // confirmation is in flight — otherwise a user can Esc / click-out
  // mid-action and the parent never knows whether to keep waiting.
  const safeClose = () => {
    if (loading) return;
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={safeClose}
      title={title}
      size="sm"
      closeOnBackdrop={!loading}
      closeOnEscape={!loading}
    >
      <div className="space-y-3" data-confirm-dialog>
        {recordName && (
          <p className="text-body-sm font-semibold text-text-heading">
            {variant === 'many-record' && recordCount !== undefined ? `${recordCount} · ` : ''}
            {recordName}
          </p>
        )}
        {description && (
          <div className="text-body-sm leading-relaxed text-text-body">{description}</div>
        )}
        {requiresTyping && (
          <div className="space-y-1.5">
            <label htmlFor={inputId} className="block text-body-sm font-medium text-statura-ink">
              Type{' '}
              <code className="rounded bg-surface-soft px-1 py-0.5 font-mono text-body-sm text-statura-ink">
                {typeToConfirmWord}
              </code>{' '}
              to confirm
            </label>
            <input
              id={inputId}
              type="text"
              value={typedValue}
              onChange={(e) => setTypedValue(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={loading}
              autoComplete="off"
              autoCorrect="off"
              spellCheck={false}
              aria-required="true"
              aria-invalid={requiresTyping && !typingValid && typedValue.length > 0}
              className={cn(
                'h-11 w-full rounded-control border bg-surface-sheet px-2.5 text-xs text-statura-ink placeholder:text-text-muted',
                'focus:outline-none focus-visible:ring-2 focus-visible:ring-focus',
                typingValid && typedValue.length > 0
                  ? 'border-status-success-border focus:border-status-success-fg'
                  : !typingValid && typedValue.length > 0
                    ? 'border-status-danger-border focus:border-status-danger-fg'
                    : 'border-border focus:border-statura-green-accessible',
                loading && 'cursor-not-allowed opacity-60',
              )}
            />
          </div>
        )}
        <DialogFooter>
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            data-ui="button"
            className={buttonVariants({ variant: 'secondary' })}
          >
            {cancelLabel}
          </button>
          {/*
            Codex Phase A Sprint 3 pass 1 P2: when the type-to-confirm
            gate is unsatisfied we use `aria-disabled` + a no-op click
            instead of the native `disabled` attribute so the Dialog's
            focus trap keeps the button in its tabbable list. Tab from
            Cancel still lands here (loops back to the input), not on
            an element outside the modal. The native `disabled`
            attribute IS still used during `loading` because a loading
              state also stays focusable but inert; otherwise focus can
              leave the Dialog while the request is in flight.
           */}
          <button
            type="button"
            onClick={() => canConfirm && !loading && onConfirm()}
            aria-disabled={!canConfirm || loading}
            data-ui="button"
            className={buttonVariants({ variant: variant === 'simple' ? 'primary' : 'danger' })}
          >
            {confirmLabel}
            {loading ? '…' : ''}
          </button>
        </DialogFooter>
      </div>
    </Dialog>
  );
}
